import { format, parseISO, addDays, startOfWeek, endOfWeek, isToday, isSameDay, isBefore, isAfter } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import i18n from '../i18n';

/**
 * Get the current locale for date-fns based on i18n language
 * @returns {Locale} date-fns locale object
 */
export const getDateLocale = () => {
  const language = i18n.language || 'en';
  return language === 'pl' ? pl : enUS;
};

/**
 * Format date to display string with locale support
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string (default: 'PP')
 * @param {Locale} locale - Optional locale override
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr = 'PP', locale = null) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr, { locale: locale || getDateLocale() });
};

/**
 * Format time to display string with locale support
 * @param {Date|string} date - Date/time to format
 * @param {string} formatStr - Format string (default: 'p')
 * @param {Locale} locale - Optional locale override
 * @returns {string} Formatted time string
 */
export const formatTime = (date, formatStr = 'p', locale = null) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr, { locale: locale || getDateLocale() });
};

/**
 * Format date and time together with locale support
 * @param {Date|string} date - Date/time to format
 * @param {Locale} locale - Optional locale override
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, locale = null) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'PPp', { locale: locale || getDateLocale() });
};

/**
 * Get the start of the current week
 * @param {Date} date - Reference date
 * @returns {Date} Start of week
 */
export const getWeekStart = (date = new Date()) => startOfWeek(date, { weekStartsOn: 1 });

/**
 * Get the end of the current week
 * @param {Date} date - Reference date
 * @returns {Date} End of week
 */
export const getWeekEnd = (date = new Date()) => endOfWeek(date, { weekStartsOn: 1 });

/**
 * Get array of dates for a week
 * @param {Date} startDate - Start date of the week
 * @returns {Date[]} Array of 7 dates
 */
export const getWeekDays = (startDate = getWeekStart()) => {
  return Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
};

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const checkIsToday = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return isToday(dateObj);
};

/**
 * Check if two dates are the same day
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDayCheck = (date1, date2) => {
  const d1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return isSameDay(d1, d2);
};

/**
 * Check if a date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return isBefore(dateObj, new Date());
};

/**
 * Check if a date is in the future
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the future
 */
export const isFutureDate = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return isAfter(dateObj, new Date());
};

/**
 * Format duration in minutes to human-readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
};
